import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Users,
  Sparkles,
  LogIn,
  User,
  Calendar,
  Mail,
} from "lucide-react";
import { useOrganization } from "../context/OrganizationContext";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

function AcceptInvite() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { acceptInvite, organization } = useOrganization();

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [isAccepting, setIsAccepting] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);

  // Get token from URL
  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  // Fetch invite info on load
  useEffect(() => {
    if (token) {
      fetchInviteInfo();
    } else {
      setStatus("error");
      setMessage("Invalid or missing invite token");
    }
  }, [token]);

  const fetchInviteInfo = async () => {
    try {
      const response = await api.get(
        `/api/v1/organizations/invites/validate?token=${token}`,
      );
      setInviteInfo(response.data);
      setStatus("ready");
    } catch (error) {
      console.error("Error fetching invite:", error);
      setStatus("error");
      setMessage(error.response?.data?.detail || "Invalid or expired invite");
    }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      sessionStorage.setItem("pendingInviteToken", token);
      navigate(`/login?redirect=/accept-invite?token=${token}`);
      return;
    }

    setIsAccepting(true);
    const result = await acceptInvite(token);

    if (result.success) {
      setStatus("success");
      setMessage("You have successfully joined the organization!");
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } else {
      setStatus("error");
      setMessage(
        result.error || "Failed to accept invite. The invite may have expired.",
      );
    }
    setIsAccepting(false);
  };

  // If user just logged in and there's a pending invite
  useEffect(() => {
    if (isAuthenticated && token) {
      const pendingToken = sessionStorage.getItem("pendingInviteToken");
      if (pendingToken === token) {
        sessionStorage.removeItem("pendingInviteToken");
        handleAccept();
      }
    }
  }, [isAuthenticated, token]);

  // Show loading state while auth is loading
  if (authLoading || status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-2xl animate-pulse" />
            <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-cyan-400 animate-spin mx-auto mb-4 relative" />
          </div>
          <p className="text-gray-400 font-medium">Loading invite...</p>
        </div>
      </div>
    );
  }

  // If invite info is loaded and valid
  if (status === "ready" && inviteInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg blur-xl opacity-50 animate-pulse" />
                <Users className="w-12 h-12 text-cyan-400 relative" />
              </div>
              <span className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                FrankTech
              </span>
              <Sparkles className="w-4 h-4 text-cyan-400" />
            </div>
            <p className="text-gray-400">AI-powered error monitoring</p>
          </div>

          {/* Invite Card */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-8 shadow-xl shadow-cyan-500/5">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-cyan-400" />
              </div>

              <h2 className="text-2xl font-bold text-white">
                You've Been Invited!
              </h2>
              <p className="text-gray-400 mt-1">
                Join{" "}
                <strong className="text-white">
                  {inviteInfo.organization_name}
                </strong>
              </p>
            </div>

            {/* Invite Details */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <User className="w-4 h-4 text-cyan-400" />
                <span>
                  Invited by:{" "}
                  <strong className="text-white">
                    {inviteInfo.invited_by_name || inviteInfo.invited_by_email}
                  </strong>
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Mail className="w-4 h-4 text-cyan-400" />
                <span>
                  Email:{" "}
                  <strong className="text-white">{inviteInfo.email}</strong>
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Calendar className="w-4 h-4 text-cyan-400" />
                <span>
                  Role:{" "}
                  <strong className="text-white capitalize">
                    {inviteInfo.role}
                  </strong>
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <AlertCircle className="w-4 h-4 text-cyan-400" />
                <span>
                  Expires:{" "}
                  <strong className="text-white">
                    {new Date(inviteInfo.expires_at).toLocaleDateString()}
                  </strong>
                </span>
              </div>
            </div>

            <div className="space-y-4">
              {!isAuthenticated && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 text-yellow-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Please log in to accept this invite.</span>
                </div>
              )}

              {isAuthenticated && organization && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 text-blue-400 text-sm">
                  You are currently in <strong>{organization.name}</strong>.
                  Accepting this invite will switch you to{" "}
                  <strong>{inviteInfo.organization_name}</strong>.
                </div>
              )}

              <button
                onClick={handleAccept}
                disabled={isAccepting}
                className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAccepting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Accepting...
                  </span>
                ) : isAuthenticated ? (
                  "Accept Invite"
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Login to Accept
                  </span>
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              You'll be able to access shared projects and collaborate with your
              team.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error or other states
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg blur-xl opacity-50 animate-pulse" />
              <Users className="w-12 h-12 text-cyan-400 relative" />
            </div>
            <span className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              FrankTech
            </span>
            <Sparkles className="w-4 h-4 text-cyan-400" />
          </div>
          <p className="text-gray-400">AI-powered error monitoring</p>
        </div>

        <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-8 shadow-xl shadow-cyan-500/5">
          <div className="text-center mb-6">
            {status === "success" ? (
              <div className="w-16 h-16 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            )}

            <h2 className="text-2xl font-bold text-white">
              {status === "success" ? "Invite Accepted!" : "Invite Failed"}
            </h2>
            <p className="text-gray-400 mt-1">{message}</p>
          </div>

          {status === "success" && (
            <button
              onClick={() => navigate("/dashboard")}
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 hover:scale-[1.02]"
            >
              Go to Dashboard
            </button>
          )}

          {status === "error" && (
            <button
              onClick={() => navigate("/")}
              className="w-full py-3 bg-gray-800/50 text-white font-medium rounded-xl hover:bg-gray-800/70 transition-all duration-200"
            >
              Go Home
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AcceptInvite;
